import * as Model from "@effect/sql/Model";
import { Schema } from "effect";

/**
 * What makes an outcome fire again (OL-67).
 *
 * - `merge`: a pull request merged into the watched branch of the watched repo
 * - `push`: a commit landed on the watched branch of the watched repo
 * - `cron`: a wall-clock schedule
 * - `manual`: an operator pressed `admin.outcomes.fire`
 *
 * Stored as plain varchar, never a pgEnum — adding a kind must not require an
 * `ALTER TYPE` and the two-deploy enum-value-isolation dance (M6).
 *
 * @since 1.0.0
 */
export const TriggerKind = Schema.Literal("merge", "push", "cron", "manual");
export type TriggerKind = Schema.Schema.Type<typeof TriggerKind>;

/**
 * The per-kind configuration of a trigger, as one open-coded jsonb struct
 * rather than four columns that are null three times out of four.
 *
 * - `repo` / `branch`: the `merge` / `push` coordinate. `repo` must be one the
 *   customer owns in `orca_repos` — checked when the trigger is armed AND again
 *   when a webhook fires it, so a later repo reassignment cannot leak.
 * - `cron` / `tz`: the `cron` schedule, parsed with Effect's `Cron.parse`.
 *
 * Every field is optional because a `manual` trigger configures nothing.
 *
 * @since 1.0.0
 */
export const TriggerSpec = Schema.Struct({
  repo: Schema.optional(Schema.String),
  branch: Schema.optional(Schema.String),
  cron: Schema.optional(Schema.String),
  tz: Schema.optional(Schema.String),
});
export type TriggerSpec = Schema.Schema.Type<typeof TriggerSpec>;

/**
 * Trigger model — what makes a standing outcome fire again (OL-67).
 *
 * A fire is a NEW `OutcomeWorkflow` execution for the SAME outcome, with a
 * fresh `attempt` ulid. Because each fire mints its own attempt, the workflow
 * engine's idempotency key differs by construction and cannot dedupe two fires
 * — the compare-and-set on the OUTCOME row (`transitionOutcomeStatus`) is the
 * only duplicate guard.
 *
 * `pendingFireId` is the coalesce queue, and it is exactly one slot deep: a
 * fire that lands while the outcome is already in flight records its id here
 * and dispatches nothing; a second fire finds the slot taken and writes
 * nothing at all. The drain tick redeems it once the outcome leaves flight,
 * re-using the SAME id as the `attempt`, so a retried drain is idempotent.
 *
 * `customerId` is a plain string with no foreign key, exactly as on `outcomes`:
 * `customers` lives in `public` while this table lives in `orcaloops`.
 *
 * @since 1.0.0
 */
export class Trigger extends Model.Class<Trigger>("Trigger")({
  id: Model.Generated(Schema.String),

  customerId: Schema.String,
  outcomeId: Schema.String,

  kind: TriggerKind,
  spec: TriggerSpec,

  /** `false` after `admin.outcomes.pauseTriggers`; the row is kept, not deleted. */
  enabled: Schema.Boolean,

  /** When this trigger last claimed a fire. `null` until the first one. */
  lastFiredAt: Schema.NullOr(Schema.DateTimeUtc),
  /**
   * When the cron tick should next consider this trigger. `null` for the
   * event-driven kinds (`merge` / `push` / `manual`), which are never due.
   */
  nextFireAt: Schema.NullOr(Schema.DateTimeUtc),
  /** The single coalesced fire waiting for the outcome to leave flight. */
  pendingFireId: Schema.NullOr(Schema.String),

  createdAt: Model.DateTimeInsertFromDate,
  updatedAt: Model.DateTimeUpdateFromDate,
}) {}
